import React, {PropsWithChildren, ReactElement, useCallback} from 'react'
import {TableInstance} from 'react-table'
import Tip, {TipType} from "../Tip";
import TablePaginator from "./TablePaginator";
import Button from "../Button";

interface Props<TP extends Record<string, unknown>> {
  instance: TableInstance<TP>,
  total: number,
  loading: boolean,
  onReset?: () => void
  error?: string
}

export function TablePagination<TP extends Record<string, unknown>>({
                                                                      instance,
                                                                      total,
                                                                      loading,
                                                                      error,
                                                                      onReset
                                                                    }: PropsWithChildren<Props<TP>>): ReactElement | null {
  const {
    state: {pageIndex, pageSize},
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    canNextPage,
    canPreviousPage,
    pageOptions
  } = instance;

  const reset = useCallback(() => {
    gotoPage(0)
    if (onReset) {
      onReset()
    }
  }, [onReset]);

  const pageCount = Math.ceil(total / pageSize);

  const handleChangePage = useCallback(
    (newPage: number) => {
      if (newPage === pageIndex + 1) {
        nextPage()
      } else if (newPage === pageIndex - 1) {
        previousPage()
      } else {
        gotoPage(newPage)
      }
    },
    [gotoPage, nextPage, pageIndex, previousPage]
  )

  const onChangeRowsPerPage = useCallback(
    (value: number) => {
      setPageSize(value)
    },
    [setPageSize, total]
  )
  const buttons = pageIndex <= 4 ? pageOptions.slice(0, 5) : pageOptions.slice(pageIndex - 4, pageIndex + 1)

  return total ? (
    <>
      {error &&
        <Tip type={TipType.Error} className="items-center">
          <div className="flex flex-row items-center">
            <div>There was an error while loading data from the server. Records for this page are not loaded.</div>
            <div className="ml-6">
              {onReset &&
                <Button onClick={() => reset()} loading={loading} short={true}
                        className="py-1 bg-red text-white text-sm">Reset</Button>
              }
            </div>
          </div>
        </Tip>
      }
      <TablePaginator
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        total={total}
        pageButtons={buttons}
        pageCount={pageCount}
        pageSize={pageSize}
        page={pageIndex}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onPageChange={handleChangePage}
        loading={loading}
      />
    </>
  ) : null
}
